/**
 * QuickActionsMenu — dropdown "+ Nieuw" rechtsboven in de topbar.
 *
 * Actie-lijst is gedreven door QUICK_ACTIONS uit nav-config.js. Iedere actie:
 *   - respecteert permissies (getoond alleen als user.hasPermission(perm))
 *   - navigeert naar het target section-key
 *   - zet optioneel window.location.hash zodat de section zijn eigen "nieuw" dialog
 *     kan openen (dit is een lichte contract-vorm — niet-implementerende secties
 *     tonen simpelweg de standaardweergave).
 *
 * Op mobiel: dropdown transformeert tot bottom-sheet (via CSS media queries).
 */
import React, { useEffect, useRef, useState } from "react";
import { Plus, Zap } from "lucide-react";
import { QUICK_ACTIONS } from "./nav-config";
import { useAuth } from "../../../context/AuthContext";

export default function QuickActionsMenu({ onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { hasPermission, user } = useAuth();

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  const visibleActions = QUICK_ACTIONS.filter(
    (a) => !a.perm || hasPermission(a.perm) || (user?.role === "owner"),
  );

  const handleClick = (a) => {
    if (a.hash) window.location.hash = a.hash;
    onAction?.(a);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white pl-2.5 pr-3 py-1.5 rounded-md text-sm font-semibold shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        data-testid="quick-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Nieuw</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.12)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150"
          role="menu"
          data-testid="quick-actions-menu"
        >
          <div className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-[0.15em] border-b border-slate-100 flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-brand-500" />
            Snelle acties
          </div>
          <ul className="py-1">
            {visibleActions.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.key}>
                  <button
                    type="button"
                    onClick={() => handleClick(a)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-brand-50 hover:text-brand-900 transition-colors duration-100"
                    data-testid={`quick-action-${a.key}`}
                    role="menuitem"
                  >
                    <span className="w-7 h-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="flex-1 text-left">{a.label}</span>
                  </button>
                </li>
              );
            })}
            {visibleActions.length === 0 && (
              <li className="px-3 py-2 text-xs text-slate-400 italic">
                Geen snelle acties beschikbaar voor jouw rol.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
